import React, { useState, useEffect } from "react";
import { manageProductApi } from "../../../entities/product";
import { Product } from "../../../entities/product";
import FileUpload from "../../../shared/ui/file-uploader/FileUpload";
import { getApiClient } from "../../../shared/api/client.api";
import "./ProductManagement.scss";

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<
    "nameKo" | "createdAt" | "numOfArticles"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    nameKo: "",
    nameEn: "",
    imageUrl: "",
    numOfArticles: 0,
  });

  // 이미지 업로드 상태
  const [uploadingImage, setUploadingImage] = useState(false);
  const [, setSelectedImageFile] = useState<File | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const productsData = await manageProductApi.getProductList();
      setProducts(productsData);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await manageProductApi.deleteProduct(id);
      setProducts(products.filter((product) => product.id !== id));
      setShowDeleteModal(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Failed to delete product:", error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedProducts.map((id) => manageProductApi.deleteProduct(id)),
      );
      setProducts(
        products.filter((product) => !selectedProducts.includes(product.id)),
      );
      setSelectedProducts([]);
    } catch (error) {
      console.error("Failed to delete products:", error);
    }
  };

  const handleAddProduct = async () => {
    try {
      await manageProductApi.addProduct(formData);
      await fetchProducts();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error("Failed to add product:", error);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;

    try {
      await manageProductApi.updateProduct(editingProduct.id, formData);
      await fetchProducts();
      setShowEditModal(false);
      setEditingProduct(null);
      resetForm();
    } catch (error) {
      console.error("Failed to update product:", error);
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      nameKo: product.nameKo,
      nameEn: product.nameEn || "",
      imageUrl: product.imageUrl || "",
      numOfArticles: product.numOfArticles,
    });
    setSelectedImageFile(null); // 기존 이미지 파일 초기화
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      nameKo: "",
      nameEn: "",
      imageUrl: "",
      numOfArticles: 0,
    });
    setSelectedImageFile(null);
  };

  const handleImageUpload = async (file: File | null) => {
    if (!file) return;

    try {
      setUploadingImage(true);
      setSelectedImageFile(file);

      const apiClient = getApiClient();
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      uploadFormData.append("type", "product");

      const response = await apiClient.post("/upload/image", uploadFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        const uploadedImageUrl = response.data.data.url;
        setFormData({ ...formData, imageUrl: uploadedImageUrl });
      } else {
        throw new Error("이미지 업로드에 실패했습니다.");
      }
    } catch (error) {
      console.error("Failed to upload image:", error);
      alert("이미지 업로드에 실패했습니다.");
    } finally {
      setUploadingImage(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.nameKo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.nameEn &&
        product.nameEn.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "nameKo":
        aValue = a.nameKo;
        bValue = b.nameKo;
        break;
      case "createdAt":
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case "numOfArticles":
        aValue = a.numOfArticles;
        bValue = b.numOfArticles;
        break;
      default:
        return 0;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = sortedProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(paginatedProducts.map((product) => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, id]);
    } else {
      setSelectedProducts(
        selectedProducts.filter((productId) => productId !== id),
      );
    }
  };

  if (loading) {
    return (
      <div className="product-management">
        <div className="product-management__header">
          <h1>제품 관리</h1>
          <p>제품을 관리하고 편집하세요.</p>
        </div>
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="product-management">
      <div className="product-management__header">
        <h1>제품 관리</h1>
        <p>제품을 관리하고 편집하세요.</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="product-management__filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="제품 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="sort-box">
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(
                e.target.value as "nameKo" | "createdAt" | "numOfArticles",
              )
            }
          >
            <option value="nameKo">이름순</option>
            <option value="createdAt">생성일순</option>
            <option value="numOfArticles">아티클 수순</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
          >
            <option value="asc">오름차순</option>
            <option value="desc">내림차순</option>
          </select>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div className="product-management__actions">
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          제품 추가
        </button>
        {selectedProducts.length > 0 && (
          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            선택 삭제 ({selectedProducts.length})
          </button>
        )}
      </div>

      {/* 제품 테이블 */}
      <div className="product-management__table">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedProducts.length === paginatedProducts.length &&
                    paginatedProducts.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>이미지</th>
              <th>한국어 이름</th>
              <th>영어 이름</th>
              <th>관련 아티클 수</th>
              <th>생성일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((product) => (
              <tr key={product.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={(e) =>
                      handleSelectProduct(product.id, e.target.checked)
                    }
                  />
                </td>
                <td className="image-cell">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.nameKo}
                      className="product-image"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="no-image">이미지 없음</div>
                  )}
                </td>
                <td>{product.nameKo}</td>
                <td>{product.nameEn || "-"}</td>
                <td>{product.numOfArticles}</td>
                <td>{new Date(product.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openEditModal(product)}
                  >
                    수정
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setProductToDelete(product.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="product-management__pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          이전
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          다음
        </button>
      </div>

      {/* 제품 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>제품 추가</h3>
              <button onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>한국어 이름 *</label>
                <input
                  type="text"
                  value={formData.nameKo}
                  onChange={(e) =>
                    setFormData({ ...formData, nameKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 이름</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>이미지</label>
                <div className="image-upload-container">
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    className="product-image-upload"
                    disabled={uploadingImage}
                    currentImageUrl={formData.imageUrl}
                  />
                  {uploadingImage && (
                    <div className="uploading-indicator">업로드 중...</div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddProduct}
                disabled={!formData.nameKo}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 제품 수정 모달 */}
      {showEditModal && editingProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>제품 수정</h3>
              <button onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>한국어 이름 *</label>
                <input
                  type="text"
                  value={formData.nameKo}
                  onChange={(e) =>
                    setFormData({ ...formData, nameKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 이름</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>이미지</label>
                <div className="image-upload-container">
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    className="product-image-upload"
                    disabled={uploadingImage}
                    currentImageUrl={formData.imageUrl}
                  />
                  {uploadingImage && (
                    <div className="uploading-indicator">업로드 중...</div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                  min="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateProduct}
                disabled={!formData.nameKo}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>삭제 확인</h3>
              <button onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {productToDelete ? (
                <p>선택한 제품을 삭제하시겠습니까?</p>
              ) : (
                <p>
                  선택한 {selectedProducts.length}개의 제품을 삭제하시겠습니까?
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (productToDelete) {
                    handleDeleteProduct(productToDelete);
                  } else {
                    handleBulkDelete();
                  }
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
